// src/util/userHelpers.js

import { EXTENDED_DATA_SCHEMA_TYPES } from './types';
import { getFieldValue } from './fieldHelpers';

/**
 * Get the namespaced attribute key based on the specified extended data scope and attribute key.
 *
 * @param {String} scope - Extended data scope (e.g., 'public', 'private', 'protected', 'meta').
 * @param {String} key - Attribute key in extended data.
 * @returns {String} A string containing the namespace prefix and the attribute name.
 */
export const addScopePrefix = (scope, key) => {
  const scopeFnMap = {
    private: (k) => `priv_${k}`,
    protected: (k) => `prot_${k}`,
    public: (k) => `pub_${k}`,
    meta: (k) => `meta_${k}`,
  };

  const validKey = key.replace(/\s/g, '_');
  const keyScoper = scopeFnMap[scope];

  return keyScoper ? keyScoper(validKey) : validKey;
};

/**
 * Pick extended data fields from given form data.
 * Picking is based on extended data configuration for the user and target scope and user type.
 *
 * This expects submit data to be namespaced (e.g., 'pub_') and it returns the field without that namespace.
 * This function is used when form submit values are restructured for the actual API endpoint.
 *
 * Note: This returns null for those fields that are managed by configuration, but don't match target user type.
 *       These might exist if user swaps between user types before saving the user.
 *
 * @param {Object} data - Values submitted from the form.
 * @param {String} targetScope - The scope of extended data to filter (e.g., 'public').
 * @param {String} targetUserType - User type to filter fields (e.g., 'APP').
 * @param {Array} userFieldConfigs - Configuration for user fields.
 * @returns {Object} An object containing the picked extended data fields.
 */
export const pickUserFieldsData = (data, targetScope, targetUserType, userFieldConfigs) => {
  return userFieldConfigs.reduce((fields, field) => {
    const { key, userTypeConfig, scope = 'public', schemaType } = field || {};
    const namespacedKey = addScopePrefix(scope, key);

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetUserType =
      !userTypeConfig.limitToUserTypeIds || userTypeConfig.userTypeIds.includes(targetUserType);

    if (isKnownSchemaType && isTargetScope && isTargetUserType) {
      const fieldValue = getFieldValue(data, namespacedKey);
      return { ...fields, [key]: fieldValue };
    } else if (isKnownSchemaType && isTargetScope && !isTargetUserType) {
      // Clears extra custom fields if user type doesn't match
      return { ...fields, [key]: null };
    }
    return fields;
  }, {});
};

/**
 * Pick extended data fields from given extended data of the user entity.
 * Picking is based on extended data configuration for the user and target scope and user type.
 *
 * This returns namespaced (e.g., 'pub_') initial values for the form.
 *
 * @param {Object} data - Existing user data.
 * @param {String} targetScope - The scope of extended data to filter (e.g., 'public').
 * @param {String} targetUserType - User type to filter fields (e.g., 'APP').
 * @param {Array} userFieldConfigs - Configuration for user fields.
 * @returns {Object} An object containing the initial values for the form fields.
 */
export const initialValuesForUserFields = (
  data,
  targetScope,
  targetUserType,
  userFieldConfigs
) => {
  return userFieldConfigs.reduce((fields, field) => {
    const { key, userTypeConfig, scope = 'public', schemaType } = field || {};
    const namespacedKey = addScopePrefix(scope, key);

    const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
    const isTargetScope = scope === targetScope;
    const isTargetUserType =
      !userTypeConfig?.limitToUserTypeIds || userTypeConfig?.userTypeIds?.includes(targetUserType);

    if (isKnownSchemaType && isTargetScope && isTargetUserType) {
      const fieldValue = getFieldValue(data, key);
      return { ...fields, [namespacedKey]: fieldValue };
    }
    return fields;
  }, {});
};

/**
 * Returns props for custom user fields.
 *
 * @param {Array} userFieldsConfig - Configuration for user fields.
 * @param {Object} intl - Internationalization object for translating messages.
 * @param {String|null} userType - User type to filter fields. If null, includes fields applicable to all types.
 * @param {Boolean} isSignup - Flag indicating if the context is a signup form.
 * @returns {Array} An array of props for custom user fields.
 */
export const getPropsForCustomUserFieldInputs = (
  userFieldsConfig,
  intl,
  userType = null,
  isSignup = true
) => {
  return (
    userFieldsConfig?.reduce((pickedFields, fieldConfig) => {
      const { key, userTypeConfig, schemaType, scope, saveConfig = {} } = fieldConfig || {};
      const namespacedKey = addScopePrefix(scope, key);
      const showField = isSignup ? saveConfig.displayInSignUp : true;

      const isKnownSchemaType = EXTENDED_DATA_SCHEMA_TYPES.includes(schemaType);
      const isTargetUserType =
        !userTypeConfig?.limitToUserTypeIds || userTypeConfig?.userTypeIds?.includes(userType);
      const isUserScope = ['public', 'private', 'protected'].includes(scope);

      if (isKnownSchemaType && isTargetUserType && isUserScope && showField) {
        // Ensure that the label is a string using intl
        const label = intl.formatMessage({ id: `GetStartedForm.${key}Label` });

        return [
          ...pickedFields,
          {
            key: namespacedKey,
            name: namespacedKey,
            label: label, // Ensured label is a string
            fieldConfig: fieldConfig,
            defaultRequiredMessage: intl.formatMessage({
              id: 'CustomExtendedDataField.required',
            }),
          },
        ];
      }

      return pickedFields;
    }, []) || []
  );
};

/**
 * Check if currentUser has permission to post listings.
 *
 * @param {Object} currentUser - API entity representing the current user.
 * @returns {Boolean} True if currentUser has permission to post listings; otherwise, false.
 */
export const hasPermissionToPostListings = (currentUser) => {
  if (currentUser?.id && !currentUser?.effectivePermissionSet?.id) {
    console.warn(
      '"effectivePermissionSet" relationship is not defined or included in the fetched currentUser entity.'
    );
  }
  return currentUser?.effectivePermissionSet?.attributes?.postListings === 'permission/allow';
};

/**
 * Determine if the current user is authorized based on their state and permissions.
 *
 * Users in the 'active' state with the necessary permissions are considered authorized.
 *
 * @param {Object} currentUser - API entity representing the current user.
 * @param {Object} [permissionsToCheck] - Specific permissions to verify (e.g., { postListings: true }).
 * @returns {Boolean} True if the user is authorized; otherwise, false.
 */
export const isUserAuthorized = (currentUser, permissionsToCheck) => {
  const { postListings } = permissionsToCheck || {};
  const isActive = currentUser?.attributes?.state === 'active';
  return permissionsToCheck && postListings
    ? isActive && hasPermissionToPostListings(currentUser)
    : isActive;
};
